import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';


// Componentes 


// Funciones para obtener datos
import ObtenerServicio from '../functions/ObtenerServicio';
import InsertarServicio from '../functions/InsertarServicio';
import GuardarServicio from '../functions/GuardarServicio';
import buildImageSrc from '../functions/buildImageSrc';

// Tipos de datos
import ServiciosEditar from '../types/ServiciosEditar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarServicioProps {
    idServicio: number;
    guardarCambios: (Servicio: ServiciosEditar) => void;
    cancelarCambios: () => void;
    eliminando: boolean;
}
  
export const EditarServicio: React.FC<EditarServicioProps> = ({ idServicio, guardarCambios, cancelarCambios, eliminando = false }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [Servicio, setServicio] = useState<ServiciosEditar | null>(null);
    
    const [errorServicio, setErrorServicio] = useState<string | null>(null);

    const [localId, setLocalId] = useState(0);
    const [localNombre, setLocalNombre] = useState('');
    const [localDescripcion, setLocalDescripcion] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localMostrarWeb, setLocalMostrarWeb] = useState('');
    const [localFchCrea, setLocalFchCrea] = useState('');
    const [localFchModi, setLocalFchModi] = useState('');
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localFchBaja, setLocalFchBaja] = useState('');
    const [localLogoMime, setLocalLogoMime] = useState<string | null>(null);
    const [localLogoContent, setLocalLogoContent] = useState<string | null>(null);
    const [localLogoName, setLocalLogoName] = useState<string | null>(null);

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);

    const fileToBase64 = (file: File): Promise<{base64: string; mime: string; name: string}> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);

            reader.onload = () => {
                const result = reader.result as string;
                const [meta, base64] = result.split(',');

                // meta = "data:image/png;base64"
                const mime = meta.split(':')[1].split(';')[0];

                resolve({ base64, mime, name: file.name });
            }

            reader.onerror = reject;
        });
    };

    useEffect(() => {
        
        const fetchServicio = async () => {
            try {

                const data = await ObtenerServicio(idServicio);

                if (data.servicios_ok === 'si') {

                    // Todo ok, prestador recuperado

                    const ServicioRecuperado: ServiciosEditar = {
                        id: data.servicios[0].id,
                        nombre: data.servicios[0].nombre,
                        descripcion: data.servicios[0].descripcion,
                        habilitado: data.servicios[0].habilitado,
                        mostrar_web: data.servicios[0].mostrar_web,
                        fecha_creacion: data.servicios[0].fecha_creacion,
                        fecha_modificacion: data.servicios[0].fecha_modificacion,
                        dado_de_baja: data.servicios[0].dado_de_baja,
                        fecha_baja: data.servicios[0].fecha_baja,
                        logo_mime: data.servicios[0].logo_mime,
                        logo_content: data.servicios[0].logo_content,
                        logo_name: data.servicios[0].logo_name,
                        imageSrc: buildImageSrc(data.servicios[0].logo_mime, data.servicios[0].logo_content) || null

                    };

                    setImagePreview(ServicioRecuperado.imageSrc ?? null);

                    setServicio(ServicioRecuperado);

                    setLocalId(data.servicios[0].id);
                    setLocalNombre(data.servicios[0].nombre);
                    setLocalDescripcion(data.servicios[0].descripcion);
                    setLocalHabilitado(data.servicios[0].habilitado);
                    setLocalMostrarWeb(data.servicios[0].mostrar_web);
                    setLocalFchCrea(data.servicios[0].fecha_creacion);
                    setLocalFchModi(data.servicios[0].fecha_modificacion);
                    setLocalDadoBaja(data.servicios[0].dado_de_baja);
                    setLocalFchBaja(data.servicios[0].fecha_baja);
                    setLocalLogoMime(data.servicios[0].logo_mime);
                    setLocalLogoContent(data.servicios[0].logo_content);
                    setLocalLogoName(data.servicios[0].logo_name);

                    setErrorServicio(null);
                } else {
                    
                    // Hubo errores al recuperar el Servicio
                    setErrorServicio(data.mensaje);
                    setLocalId(0);
                    setServicio(null);
                    setLocalNombre('');
                    setLocalDescripcion('');
                    setLocalHabilitado('');
                    setLocalMostrarWeb('');
                    setLocalFchCrea('');
                    setLocalFchModi('');
                    setLocalDadoBaja('');
                    setLocalFchBaja('');
                    setLocalLogoMime(null);
                    setLocalLogoContent(null);
                    setLocalLogoName(null);
                }

                setIsLoading(false);
                                
            } catch (err) {
                setErrorServicio('Error al obtener la información del Servicio.');
                setLocalId(0);
                setServicio(null);
                setLocalNombre('');
                setLocalDescripcion('');
                setLocalHabilitado('');
                setLocalMostrarWeb('');
                setLocalFchCrea('');
                setLocalFchModi('');
                setLocalDadoBaja('');
                setLocalFchBaja('');
                setLocalLogoMime(null);
                setLocalLogoContent(null);
                setLocalLogoName(null);

                setIsLoading(false);
            }
        };

        if (idServicio > 0) {
            // Estoy editando un Servicio existente
            fetchServicio();
        } else {
            // Estoy creando un nuevo Servicio
            setIsLoading(false);
            setServicio({id:0, nombre:'', descripcion:'', habilitado:'', mostrar_web: '', fecha_creacion:'', fecha_modificacion:'', dado_de_baja:'', fecha_baja:'', logo_mime:null, logo_content:null, logo_name:null, imageSrc: null});
            setImagePreview(null);
            setLocalId(0);
            setLocalNombre('');
            setLocalDescripcion('');
            setLocalFchCrea('');
            setLocalFchModi('');
            setLocalFchBaja('');
            setLocalHabilitado('S');
            setLocalMostrarWeb('S');
            setLocalDadoBaja('N');
            setLocalLogoMime(null);
            setLocalLogoContent(null);
            setLocalLogoName(null);
        }

    }, []);

    const handleChangeLocalNombre = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombre(event.target.value);
    };

    const handleChangeLocalDescripcion = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalDescripcion(event.target.value);
    };

    const handleChangeLocalLogoMime = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoMime(event.target.value);
    };

    const handleChangeLocalLogoContent = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoContent(event.target.value);
    };

    const handleChangeLocalLogoName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalLogoName(event.target.value);
    };

    const handleChangeLocalHabilitado = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalHabilitado(valorMayuscula);
    };

    const handleChangeLocalMostrarWeb = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalMostrarWeb(valorMayuscula);
    };

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        let valor = event.target.value;
        let valorMayuscula = valor.toUpperCase();
        setLocalDadoBaja(valorMayuscula);
    };

    const handleChangeImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Solo se permiten imágenes");
            return;
        }

        setImageFile(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };

        reader.readAsDataURL(file);
    };
    
    const guardarCambiosLocal = async () => {

        let logo_name = localLogoName;
        let logo_mime = localLogoMime;
        let logo_content = localLogoContent;

        if (imageFile) {
            const result = await fileToBase64(imageFile);
            logo_name = result.name;
            logo_mime = result.mime;
            logo_content = result.base64;
        } else {
            setLocalLogoMime(Servicio?.logo_mime ?? null);
            setLocalLogoContent(Servicio?.logo_content ?? null);
            setLocalLogoName(Servicio?.logo_name ?? null);
            logo_name = localLogoName;
            logo_mime = localLogoMime;
            logo_content = localLogoContent;
        }

        if (idServicio === 0) {
            // Crear nuevo Servicio

            const ServicioNuevo: ServiciosEditar = {
                id: 0,
                nombre: localNombre,
                descripcion: localDescripcion,
                habilitado: localHabilitado,
                mostrar_web: localMostrarWeb,
                fecha_creacion: '',
                fecha_modificacion: '',
                dado_de_baja: 'N',
                fecha_baja: '',
                logo_mime: logo_mime,
                logo_content: logo_content,
                logo_name: logo_name
            };
            
            try {
                const ServicioInsertado = await InsertarServicio(ServicioNuevo);

                if (ServicioInsertado.servicio_insertado === 'si') {
                    guardarCambios(ServicioNuevo);
                } else {
                    alert('Error al guardar los cambios: ' + ServicioInsertado.mensaje);
                }
            } catch (error) {
                alert('Error al guardar los cambios: ' + error);
            }
        } else {

            if (eliminando) {
                
                // Se realiza la baja lógica del Servicio
                // HABILITADO = N
                // DADO_BAJA = S
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);

                // Modificar datos Servicio
                const ServicioEditado: ServiciosEditar = {
                    id: idServicio,
                    nombre: localNombre,
                    descripcion: localDescripcion,
                    habilitado: 'N',
                    mostrar_web: localMostrarWeb,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: 'S',
                    fecha_baja: guardarFecha,
                    logo_mime: localLogoMime,
                    logo_content: localLogoContent,
                    logo_name: localLogoName
                };

                const respuesta = await GuardarServicio(ServicioEditado);

                if (respuesta.servicio_guardado === 'si') {
                    guardarCambios(ServicioEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
                
            } else {
                
                // Modificar Servicio
                const fechaActual = new Date().toISOString();
                const guardarFecha = fechaActual.slice(0, 10)+" "+fechaActual.slice(11, 19);
                
                // Modificar datos Servicio
                const ServicioEditado: ServiciosEditar = {
                    id: idServicio,
                    nombre: localNombre,
                    descripcion: localDescripcion,
                    habilitado: localHabilitado,
                    mostrar_web: localMostrarWeb,
                    fecha_creacion: localFchCrea,
                    fecha_modificacion: guardarFecha,
                    dado_de_baja: localDadoBaja,
                    fecha_baja: localDadoBaja === 'S' ? guardarFecha : '',
                    logo_mime: logo_mime,
                    logo_content: logo_content,
                    logo_name: logo_name
                };

                const respuesta = await GuardarServicio(ServicioEditado);

                if (respuesta.servicio_guardado === 'si') {
                    guardarCambios(ServicioEditado);
                } else {
                    alert('Error al guardar los cambios: ' + respuesta.mensaje);
                }
            }
        }
    };
    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && Servicio) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-5">
                    {eliminando ? (
                        <h1 className="font-metropolis text-xl text-red-700 font-bold mb-2">Eliminando Servicio: {Servicio?.nombre}</h1>
                    ) : (
                        <h1 className="font-metropolis text-xl font-bold mb-2">{idServicio > 0 ? "Editando Servicio: " + Servicio?.nombre : "Nuevo Servicio"}</h1>
                    )}
                    {/* Campos principales */}     
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 justify-between items-start flex-wrap mb-2 pb-2">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="id" label="Id" variant="outlined" value={localId}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                disabled
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[380px] flex-grow w-full">
                            <TextField id="nombre" label="Nombre" variant="outlined" value={localNombre}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombre}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Descripción */}
                        <div className="flex flex-col justify-start items-center flex-grow w-full">
                            <TextField id="descripcion" label="Descripción" variant="outlined" value={localDescripcion}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalDescripcion}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Nombre archivo */}
                        <div className="flex flex-col justify-start items-center max-w-[200px] flex-grow w-full">
                            <TextField id="nombreArchivo" label="Nombre archivo" variant="outlined" value={localLogoName}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                    },
                                    '& .MuiInputLabel-outlined': {
                                        top: '-6px',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        top: '0px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalLogoName}
                                disabled={eliminando}
                            />
                        </div>

                        {/* Fechas */}
                        {Servicio.id > 0 && (<>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcrea" label="Fec. Creado" variant="outlined" value={localFchCrea}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchcmodi" label="Fec. Modificado" variant="outlined" value={localFchModi}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>                                    
                        </>)
                        }

                        {/* Habilitado */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Habilitado
                                    </InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabilitado}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Mostrar en web */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-mostrar-web"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Mostrar web
                                    </InputLabel>
                                <Select
                                    labelId="label-mostrar-web"
                                    id="mostrar-web"
                                    value={localMostrarWeb}
                                    label="Mostrar web"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalMostrarWeb}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Dado de baja */}
                        <div className="flex flex-col justify-between items-center max-w-[100px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-dadobaja"
                                    sx={{top: '-6px',
                                        '&.MuiInputLabel-shrink': {
                                            transform: 'translate(14px, 0px) scale(0.75)',
                                        },
                                    }}
                                    >
                                        Baja
                                    </InputLabel>
                                <Select
                                    labelId="label-dadobaja"
                                    id="dadobaja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                    disabled={eliminando}
                                    >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        {localDadoBaja === 'S' && (
                            <div className="flex flex-col justify-between items-center max-w-[170px] flex-grow w-full">
                                <TextField id="fchbaja" label="Fec. Baja" variant="outlined" value={localFchBaja}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            padding: '8px',
                                        },
                                        '& .MuiInputLabel-outlined': {
                                            top: '-6px',
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            top: '0px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                />
                            </div>)
                        }

                        {/* Preview y botón para subir imagen nueva */}
                        <div className="flex flex-col justify-start items-center max-w-[220px] flex-grow w-full">

                            {/* Label */}
                            <Typography
                                variant="caption"
                                sx={{
                                    alignSelf: 'flex-start',
                                    marginBottom: '4px',
                                    marginLeft: '4px',
                                    color: 'rgba(0, 0, 0, 0.6)'
                                }}
                            >
                                Icono:
                            </Typography>

                            {/* Preview */}
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="logo"
                                    className="w-[120px] h-[120px] object-contain border rounded mb-2"
                                />
                            ) : (
                                <div className="w-[120px] h-[120px] flex items-center justify-center border rounded mb-2 text-gray-400">
                                    Sin imagen
                                </div>
                            )}

                            {/* Botón subir */}
                            <Button
                                variant="contained"
                                component="label"
                                disabled={eliminando}
                                size="small"
                            >
                                Cambiar
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={handleChangeImage}
                                />
                            </Button>
                        </div>

                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        {eliminando ? (
                            <>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={guardarCambiosLocal} ><DeleteForeverIcon /> Eliminar</button>
                                <button className="mt-3 vp-btn-azul-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        ) : (
                            <>
                                <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                                <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                            </>
                        )}
                        
                    </div>
                </div>
            )}
        </>
    );
};
